import { errorMsg } from '../utils/errorMsg';
import React, { useState, useEffect } from 'react';
import { Box, Grid, Card, Typography, CircularProgress, Alert, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Bar, Doughnut } from 'react-chartjs-2';
import { registerCharts, barOptions, doughnutOptions, barDataset, CHART } from '../components/ChartTheme';
import AppLayout from '../components/AppLayout';
import api from '../axios';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import GroupsIcon from '@mui/icons-material/Groups';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import WarningIcon from '@mui/icons-material/Warning';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AssessmentIcon from '@mui/icons-material/Assessment';

registerCharts();

export default function ConsolidacaoDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total: 0, distribuidos: 0, nao_distribuidos: 0, contatados: 0, atrasados: 0 });
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');

  useEffect(() => {
    api.get('/visitantes/')
      .then(res => {
        const dados = res.data || [];
        const total = dados.length;
        const distribuidos = dados.filter(v => v.distribuido).length;
        const nao_distribuidos = total - distribuidos;
        const contatados = dados.filter(v => v.status_contato !== 'nao_contactado').length;
        const atrasados = dados.filter(v => v.distribuido && v.status_contato === 'nao_contactado').length;
        setStats({ total, distribuidos, nao_distribuidos, contatados, atrasados });
      })
      .catch(err => setErro(errorMsg(err) || 'Erro ao carregar'))
      .finally(() => setLoading(false));
  }, []);

  const kpis = [
    { icon: <PersonAddIcon />, value: stats.total, label: 'Visitantes' },
    { icon: <GroupsIcon />, value: stats.distribuidos, label: 'Distribuídos' },
    { icon: <ScheduleIcon />, value: stats.nao_distribuidos, label: 'Não distribuídos' },
    { icon: <CheckCircleIcon />, value: stats.contatados, label: 'Contatados' },
    { icon: <WarningIcon />, value: stats.atrasados, label: 'Atrasados' },
  ];

  const doughnutData = {
    labels: ['Distribuídos', 'Não distribuídos'],
    datasets: [{ data: [stats.distribuidos, stats.nao_distribuidos], backgroundColor: [CHART.teal, CHART.lime], borderWidth: 0, hoverOffset: 6 }],
  };

  const barLabels = ['Contatados', 'Atrasados', 'Não distribuídos', 'Distribuídos'];
  const barValues = [stats.contatados, stats.atrasados, stats.nao_distribuidos, stats.distribuidos];

  const atalhos = [
    { label: 'Gestão de Visitantes', icon: <PersonAddIcon />, path: '/gestao-visitantes' },
    { label: 'Gestão de Consolidadores', icon: <GroupsIcon />, path: '/gestao-consolidadores' },
    { label: 'Capturar Card', icon: <CreditCardIcon />, path: '/capturar-card' },
    { label: 'Relatórios', icon: <AssessmentIcon />, path: '/relatorios-consolidacao' },
  ];

  return (
    <AppLayout>
      <Box sx={{ py: { xs: 2, md: 3 } }}>
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="body2" sx={{ color: CHART.teal, fontWeight: 700, letterSpacing: 1, mb: 0.5, textTransform: 'uppercase', fontSize: 12 }}>Módulo</Typography>
            <Typography variant="h1" sx={{ fontSize: { xs: '1.8rem', md: '2.4rem' }, lineHeight: 1.2, fontWeight: 800 }}>Consolidação</Typography>
            <Typography sx={{ color: 'text.secondary', mt: 1 }}>Acompanhe visitantes, distribuição e contato desde o primeiro contato.</Typography>
          </Box>
          <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => navigate('/gestao-visitantes')} sx={{ fontWeight: 700, px: 3, py: 1.2 }}>
            Cadastrar visitante
          </Button>
        </Box>

        {loading && <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress /></Box>}
        {erro && <Alert severity="error" sx={{ mb: 3 }}>{erro}</Alert>}

        <Grid container spacing={2} sx={{ mb: 4 }}>
          {kpis.map((k, i) => (
            <Grid item xs={6} md={2.4} key={i}>
              <Card elevation={0} sx={{ bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider', p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ width: 46, height: 46, borderRadius: 1, bgcolor: 'rgba(0,241,167,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: CHART.teal, flexShrink: 0 }}>{k.icon}</Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 800, fontSize: '4.4rem', color: 'text.primary', lineHeight: 1 }}>{k.value}</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>{k.label}</Typography>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Card elevation={0} sx={{ bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider', p: 3 }}>
              <Typography variant="h3" sx={{ fontSize: '1rem', mb: 2 }}>Distribuição de visitantes</Typography>
              <Box sx={{ height: 220 }}>
                <Doughnut data={doughnutData} options={doughnutOptions()} />
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card elevation={0} sx={{ bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider', p: 3 }}>
              <Typography variant="h3" sx={{ fontSize: '1rem', mb: 2 }}>Status de acompanhamento</Typography>
              <Box sx={{ height: 220 }}>
                <Bar data={{ labels: barLabels, datasets: [{ ...barDataset(barValues), label: 'Registros' }] }} options={barOptions()} />
              </Box>
            </Card>
          </Grid>
        </Grid>

        <Typography variant="h3" sx={{ fontSize: '1rem', fontWeight: 700, mb: 2 }}>Atalhos</Typography>
        <Grid container spacing={1.5}>
          {atalhos.map(a => (
            <Grid item xs={12} sm={6} md={3} key={a.path}>
              <Button variant="outlined" onClick={() => navigate(a.path)} endIcon={<ArrowForwardIcon />} fullWidth
                sx={{ justifyContent: 'space-between', borderColor: 'rgba(0,241,167,0.4)', color: CHART.teal, fontWeight: 700, borderRadius: 1, px: 2, py: 1.2, '&:hover': { borderColor: CHART.teal, bgcolor: 'rgba(0,241,167,0.08)' } }}>
                {a.label}
              </Button>
            </Grid>
          ))}
        </Grid>
      </Box>
    </AppLayout>
  );
}
